import React, { memo, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  Alert,
  Box,
  Button,
  Container,
  Header,
  Pagination,
  Popover,
  SpaceBetween,
  StatusIndicator,
  Toggle,
} from '@amzn/awsui-components-react-v3';

import { logger, useGetUserInformation } from 'appSelectorContext';
import clsx from 'clsx';
import { isNull, omitBy, uniqBy } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { MAX_SERIALS_IN_TRANSACTION } from '../../constants';
import useLocalStorage from '../../hooks/useLocalStorage';
import { Context, Types } from '../../store';
import {
  ActiveContainerType,
  MetricPair,
  MetricSeverity,
  MetricType,
  Model,
  ReceiveType,
  ReferenceQueryResponseItem,
  Serial,
  URIParams,
  ValidationStatusType,
} from '../../store/types';
import ScanInput from '../ScanInput';
import { ErrorMessages } from './SerialErrors';
import SerialTokens from './SerialTokens';
import SerialsTable from './SerialsTable';
import { MOBILITY_TICKET_URL, SERIAL_ERROR_CODES } from './constants';
import useScanSerial from './useScanSerial';

import './scanSerials.scss';

const PAGE_SIZE = 50;
const TABLE_SIZE = 10;
const BATCH_SERIAL_SIZE = 20;
const BATCH_TIME_OUT_MS = 1000;

export interface ScanSerialsProps {
  children?: React.ReactNode;
  serials: Serial[];
  activeContainer: ActiveContainerType | null;
  modelId: string;
  referenceId: string;
  disable?: boolean;
  processing: boolean;
  overrage?: boolean;
  show: boolean;
}

const ScanSerialsHeader = memo(
  ({ orderedSerials, listView, onHandleChangeListView, currentPageIndex, pageCount, onHandlePageChange }: any) => {
    return (
      <Header
        variant="h3"
        counter={`(${orderedSerials.length})`}
        description={
          <Box color="text-status-inactive" fontSize="body-s" variant="span">
            <ErrorMessages code="serail_not_available_to_scan" />
          </Box>
        }
        actions={
          <SpaceBetween direction="horizontal" size="m">
            <Box margin={{ top: 'xs' }}>
              <Toggle ariaLabel={`list-view-${listView}`} onChange={onHandleChangeListView} checked={listView}>
                List View
              </Toggle>
            </Box>
            <Pagination
              ariaLabels={{
                nextPageLabel: 'Next page',
                previousPageLabel: 'Previous page',
                pageLabel: (pageNumber) => `Page ${pageNumber} of all pages`,
              }}
              currentPageIndex={currentPageIndex}
              onChange={({ detail }) => onHandlePageChange(detail.currentPageIndex)}
              disabled={pageCount <= 1}
              pagesCount={pageCount}
            />
          </SpaceBetween>
        }
      >
        Parts to Receive
      </Header>
    );
  },
);

const ScanSerials = ({
  activeContainer,
  serials,
  modelId,
  referenceId,
  disable = false,
  processing = false,
  overrage = false,
  show = true,
}: ScanSerialsProps) => {
  const { dispatch, state } = useContext(Context);
  const [filterbyError, setFilterByErrors] = useState(false);
  const [disableFocus, setDisableFocus] = useState(false);
  const { receiveType } = useParams<URIParams>();
  const isAdjustment = receiveType === ReceiveType.INVENTORY_ADJUSTMENT;
  const isRma = receiveType === 'no_reference';

  const [listView, setListView] = useLocalStorage('serials-table-view', false);
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [called, setCalled] = useState({});
  const [fetch, setFetch] = useState(true);
  const [duplicateScan, setDuplicateScan] = useState<string | null>(null);
  const [dismissAlert, setDismissAlert] = useState<boolean>(false);
  const [displayMobilityFailureMessage, setDisplayMobilityFailureMessage] = useState<boolean>(false);

  const model: Model = state.models.find((m: Model) => m.mpnIpnId === modelId && m?.referenceId === referenceId);
  const context = useGetUserInformation();
  const pageSize = listView ? TABLE_SIZE : PAGE_SIZE;
  const notCalledSerials = serials.filter((s) => !called[s.id] && !s.called);

  const { validateSerial, validateSerials } = useScanSerial(activeContainer?.value);

  const allSerials: Serial[] = state.serials.filter((serial: Serial) => serial.mpnIpnId === modelId);
  const MAX_SERIALS_REACHED = allSerials.length >= MAX_SERIALS_IN_TRANSACTION;
  const references: ReferenceQueryResponseItem[] = state?.receiveReferences || [];
  const reference = references.find(
    (ref: ReferenceQueryResponseItem) => ref.receiveReference.receiveReferenceId === referenceId,
  );
  const isPO =
    reference?.receiveReference.externalReferenceType &&
    [ReceiveType.ASN_AWS_PO, ReceiveType.AWS_PO].includes(reference?.receiveReference.externalReferenceType);

  const errorSerials = serials.filter(
    (s: Serial) =>
      s?.status?.validationStatusType === ValidationStatusType.ERROR ||
      s?.status?.validationStatusType === ValidationStatusType.WARNING,
  );
  const pendingSerials = serials.filter((s: Serial) => s?.loading).reverse();

  const errorsAndWarnings = [...errorSerials].reverse();

  const otherSerials = serials.filter((s: Serial) => s?.status?.validationStatusType === ValidationStatusType.OK);

  const orderedSerials = filterbyError
    ? errorsAndWarnings
    : [...pendingSerials, ...errorsAndWarnings, ...otherSerials.reverse()];

  const slicedSerials = orderedSerials.slice(
    (currentPageIndex - 1) * PAGE_SIZE,
    (currentPageIndex - 1) * PAGE_SIZE + PAGE_SIZE,
  );

  const tableSerials = orderedSerials.slice(
    (currentPageIndex - 1) * TABLE_SIZE,
    (currentPageIndex - 1) * TABLE_SIZE + TABLE_SIZE,
  );

  const pageCount = Math.ceil(orderedSerials?.length / pageSize);

  useEffect(() => {
    if (duplicateScan) {
      setDismissAlert(true);
      setTimeout(function () {
        setDismissAlert(false);
        setDuplicateScan(null);
      }, 3000);
    }
  }, [duplicateScan]);

  useEffect(() => {
    if (displayMobilityFailureMessage) {
      setDisplayMobilityFailureMessage(true);
      setTimeout(function () {
        setDisplayMobilityFailureMessage(false);
      }, 10000);
    }
  }, [displayMobilityFailureMessage]);

  useEffect(() => {
    if (fetch && notCalledSerials.length > 0) {
      const selectedSerials = notCalledSerials.slice(0, BATCH_SERIAL_SIZE);
      const selectedSerialIds = selectedSerials.reduce((conc, curr) => {
        conc[curr.id] = true;
        return conc;
      }, called);

      setFetch(false);
      const serialRequests = selectedSerials.filter((s) => !!s.request).map((s) => omitBy(s.request, isNull));

      if (serialRequests.length === 0) {
        return;
      }

      validateSerials({
        variables: {
          serials: serialRequests,
        },
        onCompleted: ({ serials }: { serials: Serial[] }) => {
          serials.forEach((serial) => {
            const externalReferenceValue: string = serial?.externalReferenceValue;
            const validationStatus = externalReferenceValue
              ? {
                  validationStatusType: ValidationStatusType.ERROR,
                  statusCode: 'SERIAL_EXPECTED_IN_SHIPMENT',
                }
              : serial.status;

            if (validationStatus.statusCode === SERIAL_ERROR_CODES.MOBILITY_SERVICE_NOT_AVAILABLE) {
              setDisplayMobilityFailureMessage(true);
            } else {
              setDisplayMobilityFailureMessage(false);
            }

            dispatch({
              type: Types.scanSerialComplete,
              payload: { ...serial, status: validationStatus },
            });
          });

          if (notCalledSerials.length >= BATCH_SERIAL_SIZE) {
            setFetch(true);
          }
        },
      });
      setCalled({ ...called, ...selectedSerialIds });

      let timer = setTimeout(() => {
        if (notCalledSerials.length > 0) {
          setFetch(true);
        }
        clearTimeout(timer);
      }, BATCH_TIME_OUT_MS);
    }
  }, [called, dispatch, serials, validateSerial, validateSerials, fetch, notCalledSerials]);

  const _handleDuplicateSerial = (serialId: string): boolean => {
    // validate if duplicate serials Scanned
    return state.serials.find((serial: Serial): boolean => {
      let isSiteSpecificSerialDuplicate: boolean = false;
      if (serial.siteSpecificSerialId) {
        isSiteSpecificSerialDuplicate =
          serial.siteSpecificSerialId?.toLowerCase() === serialId?.toLowerCase() &&
          serial.mpnIpnId?.toLowerCase() === modelId?.toLowerCase();
      }

      return (
        isSiteSpecificSerialDuplicate ||
        (serial.serialNumber?.toLowerCase() === serialId?.toLowerCase() &&
          serial.mpnIpnId?.toLowerCase() === modelId?.toLowerCase())
      );
    });
  };

  const onHandleScanSerial = (val: string) => {
    if (!modelId || !activeContainer) {
      return;
    }
    if (currentPageIndex !== 1) {
      setCurrentPageIndex(1);
    }

    if (filterbyError) {
      setFilterByErrors(false);
    }

    const validateRegex = /^\S+$/;
    const regexp = new RegExp(validateRegex);

    // uuid for serial scanned
    const id = uuidv4();

    // validate if duplicate serials Scanned
    const serialAlreadyScanned = _handleDuplicateSerial(val);

    if (serialAlreadyScanned) {
      setDuplicateScan(val);
      return;
    } else {
      setDuplicateScan(null);
    }

    // contains special characters
    if (!regexp.test(val?.toUpperCase())) {
      dispatch({
        type: Types.scanSerial,
        payload: {
          containerId: activeContainer?.value,
          serialNumber: val,
          modelId,
          id,
          referenceLineItemId: model.referenceLineItemId,
          mpnIpnId: model.mpnIpnId,
          status: {
            validationStatusType: ValidationStatusType.ERROR,
            statusCode: SERIAL_ERROR_CODES.SERIAL_SPECIAL_CHARACTERS,
            statusMessage: '',
          },
        },
      });
      return;
    }

    logger.info(
      `new serial scanned: ${JSON.stringify({
        payload: {
          containerId: activeContainer?.value,
          serialNumber: val,
          modelId,
          loading: true,
          id,
          referenceLineItemId: model.referenceLineItemId,
          mpnIpnId: model.mpnIpnId,
          request: {
            referenceId,
            referenceLineItemId: model.referenceLineItemId,
            mpn: model.modelNumber,
            manufacturer: model.manufacturer,
            ipn: model.mappedIPN,
            inventoryAdjustmentWorkflow: isAdjustment,
            rmaIdWorkflow: isRma,
            serialId: val,
            id,
          },
        },
        withUser: context.user,
      })} `,
    );

    let metricsList: MetricPair[] = [];
    /* istanbul ignore next */
    if (!isAdjustment && !isRma && overrage) {
      let metric: MetricPair = {
        severity: MetricSeverity.WARNING,
        metricType: isPO ? MetricType.OVERAGE_PO : MetricType.OVERAGE_ASN,
      };
      metricsList = metricsList.concat(metric);
    }

    dispatch({
      type: Types.scanSerial,
      payload: {
        containerId: activeContainer?.value,
        serialNumber: val,
        modelId,
        loading: true,
        id,
        referenceLineItemId: model.referenceLineItemId,
        mpnIpnId: model.mpnIpnId,
        metricsList,
        request: {
          referenceId,
          referenceLineItemId: model.referenceLineItemId,
          mpn: model.expectedMPN,
          manufacturer: model.manufacturer,
          ipn: model.mappedIPN,
          inventoryAdjustmentWorkflow: isAdjustment,
          rmaIdWorkflow: isRma,
          serialId: val,
          id,
        },
      },
    });

    if (!fetch) {
      setFetch(true);
    }
  };

  const onHandleSiteSpecificSerialScanned = (serial: Serial, siteSpecificSerialId: string) => {
    const serialAlreadyScanned = _handleDuplicateSerial(siteSpecificSerialId);
    if (serialAlreadyScanned) {
      dispatch({
        type: Types.scanSerialComplete,
        payload: {
          ...serial,
          siteSpecificSerialId,
          status: {
            validationStatusType: ValidationStatusType.ERROR,
            statusCode: 'serial_id_duplicate',
            statusMessage: '',
          },
        },
      });

      return;
    }

    logger.info(
      `site specific serial scanned: ${JSON.stringify({
        payload: {
          ...serial,
          siteSpecificSerialId,
          request: {
            mpn: model.expectedMPN,
            manufacturer: model.manufacturer,
            referenceId,
            ipn: model.mappedIPN,
            serialId: siteSpecificSerialId,
            id: serial.id,
            siteSpecificSerial: true,
          },
        },
        withUser: context.user,
      })} `,
    );
    dispatch({
      type: Types.siteSpecificSerialScanned,
      payload: {
        ...serial,
        called: false,
        siteSpecificSerialId,
        request: {
          mpn: model.expectedMPN,
          manufacturer: model.manufacturer,
          referenceId: undefined,
          ipn: model.mappedIPN,
          serialId: siteSpecificSerialId,
          id: serial.id,
          siteSpecificSerial: true,
        },
      },
    });

    setCalled({ ...called, [serial.id]: false });
    setFetch(true);
  };

  const onHandleDeleteSerial = (tokens: Serial[]) => {
    logger.info(
      `serial deleted: ${JSON.stringify({
        payload: tokens,
        withUser: context.user,
      })} `,
    );
    dispatch({
      type: Types.deleteSerial,
      payload: tokens.map((t) => ({
        containerId: t?.containerId,
        serialNumber: t?.serialNumber,
        id: t?.id,
      })),
    });
    const node = document.getElementById('scan-serial-input');
    setTimeout(() => node?.focus());
  };

  const onHandleClickSerial = () => {
    logger.info(
      `serial clicked: ${JSON.stringify({
        withUser: context.user,
      })} `,
    );
    setDisableFocus(true);
  };

  const onHandleCopy = async () => {
    const allserials = orderedSerials.reduce(
      (total, serial, index) =>
        total + (index === 0 ? '' : '\n') + (serial?.siteSpecificSerialId || serial?.serialNumber),
      '',
    );
    try {
      await navigator.clipboard.writeText(allserials);
      logger.info(
        `copied serials: ${JSON.stringify({
          mpn: model.modelNumber,
          manufacturer: model.manufacturer,
          ipn: model.mappedIPN,
          serials: allserials,
          withUser: context.user,
        })}`,
      );
    } catch (err) {
      logger.error(
        `failed to copy serials: ${JSON.stringify({
          mpn: model.modelNumber,
          manufacturer: model.manufacturer,
          ipn: model.mappedIPN,
          serials: allserials,
          withUser: context.user,
        })}`,
      );
    }
  };

  const handleOnPageChange = (currentPageIndex: number) => {
    logger.info(
      `serials pagination page changed: ${JSON.stringify({
        currentPageIndex,
        withUser: context.user,
      })} `,
    );
    setCurrentPageIndex(currentPageIndex);
  };

  if (!model || !show) {
    return null;
  }

  return (
    <Container
      header={
        <ScanSerialsHeader
          orderedSerials={orderedSerials}
          pageCount={pageCount}
          currentPageIndex={currentPageIndex}
          listView={listView}
          onHandleChangeListView={({ detail }) => {
            setCurrentPageIndex(1);
            setListView(detail.checked);
          }}
          onHandlePageChange={handleOnPageChange}
        />
      }
    >
      <div className={clsx('serials-section-main', { disabled: processing })} role="row">
        <SpaceBetween direction="vertical" size="s">
          <div className={clsx('serials-section')}>
            <SpaceBetween direction="horizontal" size="s">
              <ScanInput
                autoFocus={true}
                placeholder={
                  MAX_SERIALS_REACHED ? 'Max limit of 1000 serials per transaction is reached.' : 'Scan serial'
                }
                validationErrorText={'Scan serial should not contain special characters'}
                onScan={onHandleScanSerial}
                label={'Scan Serial on Physical Part'}
                clearOnSubmit
                disabled={processing || disable || MAX_SERIALS_REACHED}
                disableFocus={disableFocus}
                controlId="scan-serial-input"
                validateRegex="^[a-zA-Z0-9 _.,-]*$"
              />
              <Box margin={{ left: 'l', top: 'xxl' }}>
                <Toggle
                  disabled={processing}
                  onChange={({ detail }) => setFilterByErrors(detail.checked)}
                  checked={filterbyError}
                >
                  Show only serials with errors and warnings
                </Toggle>
              </Box>
              <Box margin={{ left: 'l', top: 'xl' }}>
                <Popover
                  dismissButton={false}
                  position="top"
                  size="small"
                  triggerType="custom"
                  content={<StatusIndicator type="success">Serials copied</StatusIndicator>}
                >
                  <Button iconName="copy" variant="link" onClick={onHandleCopy} disabled={serials.length === 0}>
                    Copy Serials
                  </Button>
                </Popover>
              </Box>
            </SpaceBetween>
          </div>
          {displayMobilityFailureMessage && (
            <Alert dismissible statusIconAriaLabel="Error" type="error" header="Mobility Service Unavailable">
              Mobility Service is currently experiencing a temporary issue. Please try again in a few minutes. If the
              issue persists, please cut a ticket <a href={MOBILITY_TICKET_URL}>here</a>.
            </Alert>
          )}
          {dismissAlert && (
            <Alert dismissible statusIconAriaLabel="Warning" type="warning">
              Duplicate scan of serial {duplicateScan} has been removed.
            </Alert>
          )}
        </SpaceBetween>
        {!listView && (
          <SerialTokens
            orderedSerials={uniqBy([...slicedSerials], 'id')}
            serials={serials}
            onDelete={onHandleDeleteSerial}
            containerId={activeContainer?.value || ''}
            disabled={processing}
            receiveState={model.status}
            onClickSerial={onHandleClickSerial}
            onsiteSpecificSerialScanned={onHandleSiteSpecificSerialScanned}
          />
        )}
        {listView && (
          <SerialsTable
            containerId={activeContainer?.value}
            serials={uniqBy(tableSerials, 'id')}
            disabled={processing}
            onHandleSiteSpecificSerialScanned={onHandleSiteSpecificSerialScanned}
            handleOnDeleteSerials={onHandleDeleteSerial}
          />
        )}
      </div>
    </Container>
  );
};

export default ScanSerials;
